import { useQuery } from "@apollo/client";
import { useState } from "react";
import AutoCompletePerson from "../autocomplete/AutoCompletePerson";
import { AutoCompleteItem } from "../autocomplete/interface";
import PersonIcon from "../icons/PersonIcon";
import {
  TicketHeading,
  TicketRoot,
  TicketSub,
  TicketSubItem,
} from "../ticket/Ticket";
import ValidationErrorMessage from "./ValidationErrorMessage";
import { PERSON_BY_ID_TICKETDATA } from "../../api/person/queries";
import IsRequired from "./common/IsRequired";

export default function PersonFormControl<U>({
  config,
  formik,
  disabled,
  value,
}: FormikPersonControlProps<U>) {
  const [selectedItem, setSelectedItem] = useState<
    AutoCompleteItem | undefined
  >(undefined);
  const { setFieldValue, setFieldTouched, touched, errors } = formik;
  const { id, label, isRequired } = config;
  const removePerson = () => {
    setFocusInputsOnChange(true);
    setFieldTouched(id, undefined);
    setFieldValue(id, undefined);
  };
  const { data, loading } = useQuery<{
    person: {
      id: string;
      fuldtNavn: string;
      kundeNr: string;
      personFirma: {
        id: string;
        periode: {
          tilDatoInklusiv: string;
        };
        firma: {
          navn1: string;
        };
      }[];
    };
  }>(PERSON_BY_ID_TICKETDATA, {
    variables: { id: value },
    skip: selectedItem !== undefined || !value,
  });
  const person = data ? data.person : undefined;

  const qFirma =
    person && person.personFirma
      ? person.personFirma.find(
          (f) => f.periode.tilDatoInklusiv === "9999-12-31",
        )
      : undefined;
  const qSelectedItem: AutoCompleteItem | undefined = person
    ? {
        id: person.id,
        header: `${person.fuldtNavn}`,
        subheader: `${person.kundeNr} |  ${qFirma ? qFirma.firma.navn1 : ""}`,
        icon: <PersonIcon height={26} />,
      }
    : loading
      ? {
          id: value ?? "",
          header: "Laster ...",
          subheader: "",
          icon: <PersonIcon height={26} />,
        }
      : undefined;
  const [focusInputsOnChange, setFocusInputsOnChange] = useState(false);
  return (
    <>
      <label className="form-label" htmlFor={id}>
        {label}
        {isRequired && <IsRequired />}
      </label>

      {value && qSelectedItem && (
        <PersonButton
          disabled={disabled}
          selectedItem={qSelectedItem}
          removePerson={() => {
            removePerson();
          }}
          focusOnMount={focusInputsOnChange}
        />
      )}
      {value && selectedItem && !qSelectedItem && (
        <PersonButton
          disabled={disabled}
          selectedItem={selectedItem}
          removePerson={() => {
            removePerson();
          }}
          focusOnMount={focusInputsOnChange}
        />
      )}
      {!value && (
        <AutoCompletePerson
          placeholder={config.placeholder ?? "Søg og vælg principal ..."}
          className={"w-100"}
          isInvalidClass={
            touched[id] && (errors as any)[id] ? "is-invalid" : ""
          }
          includeFirstItem={false}
          onSelectedItemChange={({ selectedItem }) => {
            if (selectedItem) {
              setFocusInputsOnChange(true);
              setFieldValue(id, selectedItem.id);
              setSelectedItem(selectedItem);
            } else {
              console.error("Item was null or undefined");
            }
          }}
          erUdoevendeAdvokat={config.erUdoevendeAdvokat}
          erFmuretssagsproeveCensor={config.erFmuretssagsproeveCensor}
          onInputBlur={() => setFieldTouched(id, true)}
          focusInputOnMount={focusInputsOnChange}
        />
      )}
      <ValidationErrorMessage
        touched={touched[id]}
        errorMessage={(errors as any)[id]}
      />
    </>
  );
}

const PersonButton = (props: {
  removePerson: () => void;
  selectedItem: AutoCompleteItem;
  disabled?: boolean;
  focusOnMount?: boolean;
}) => {
  const { removePerson, selectedItem, disabled, focusOnMount } = props;
  return (
    <div className="border rounded w-100">
      <TicketRoot
        button
        icon={disabled ? undefined : "close"}
        className={`${disabled ? " c-ticket-disabled" : ""}`}
        onClick={() => {
          if (!disabled) removePerson();
        }}
        focusOnMount={focusOnMount}
      >
        <TicketHeading>
          <h4 className="h5">{selectedItem.header}</h4>
        </TicketHeading>
        <TicketSub>
          <TicketSubItem>{selectedItem.subheader}</TicketSubItem>
        </TicketSub>
      </TicketRoot>
    </div>
  );
};
